import { useState, useEffect } from 'react';

// Tailwind breakpoints
const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

type Breakpoint = keyof typeof breakpoints;

interface ViewportState {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isAbove: (breakpoint: Breakpoint) => boolean;
  isBelow: (breakpoint: Breakpoint) => boolean;
}

/**
 * Hook to detect viewport size and provide responsive utilities
 * Matches Tailwind CSS breakpoints
 */
export function useViewport(): ViewportState {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isAbove = (breakpoint: Breakpoint): boolean => {
    return width >= breakpoints[breakpoint];
  };

  const isBelow = (breakpoint: Breakpoint): boolean => {
    return width < breakpoints[breakpoint];
  };

  return {
    width,
    height,
    isMobile: width < breakpoints.md, // < 768px
    isTablet: width >= breakpoints.md && width < breakpoints.lg, // 768px - 1023px
    isDesktop: width >= breakpoints.lg, // >= 1024px
    isAbove,
    isBelow,
  };
}
